import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useBlocker } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PencilSquareIcon,
  InboxArrowDownIcon,
  PrinterIcon,
} from '@heroicons/react/20/solid';
import VendorPicker from '../components/VendorPicker';
import POLineEditor from '../components/POLineEditor';
import POReceivingModal from '../components/POReceivingModal';
import POReceiptEditModal from '../components/POReceiptEditModal';
import PaymentReferenceModal from '../components/PaymentReferenceModal';
import PillSelect from '../components/PillSelect';
import { Input, Textarea, FormGrid, Modal, ModalFooter, ModalButton, Select } from '../components/ui';
import { generatePOPDF, PAYMENT_TERMS_OPTIONS } from '../utils/poUtils';
import { printLotLabels } from '../utils/printLotLabels';
import { useLocalDraft, readLocalDraft, clearLocalDraft } from '../hooks/useLocalDraft';

const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'issued',    label: 'Sent' },
  { value: 'received',  label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid',   label: 'Paid' },
];

const PAYMENT_PILL = {
  unpaid: 'bg-amber-50 text-amber-700 border-amber-300',
  paid:   'bg-emerald-50 text-emerald-700 border-emerald-300',
};

const STATUS_PILL = {
  draft:               'bg-slate-100 text-slate-700 border-slate-300',
  issued:              'bg-sky-50 text-sky-700 border-sky-300',
  received:            'bg-emerald-50 text-emerald-700 border-emerald-300',
  cancelled:           'bg-rose-50 text-rose-700 border-rose-300',
  partially_received:  'bg-amber-50 text-amber-700 border-amber-300',
  closed:              'bg-slate-200 text-slate-700 border-slate-300',
};
const STATUS_LABEL = {
  draft: 'Draft',
  issued: 'Sent',
  received: 'Received',
  cancelled: 'Cancelled',
  partially_received: 'Partial',
  closed: 'Closed',
};

// Forward progression ordering (excluding cancelled which is orthogonal)
const STATUS_ORDER = {
  draft: 0,
  issued: 1,
  partially_received: 2,
  received: 3,
  closed: 4,
};

const isStatusRevert = (oldStatus, newStatus) => {
  if (oldStatus === 'cancelled' || newStatus === 'cancelled') return false;
  return (STATUS_ORDER[newStatus] ?? 0) < (STATUS_ORDER[oldStatus] ?? 0);
};

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const blankLine = () => ({
  _key: `new-${Math.random().toString(36).slice(2, 9)}`,
  id: null,
  line_number: '',
  part_number: '',
  description: '',
  quantity: '',
  uom: 'ea',
  unit_price: '',
  need_by: '',
  intended_job_id: null,
  notes: '',
});

export default function PurchaseOrder() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [po, setPo] = useState(null);
  const [lines, setLines] = useState([]);
  const [deletedLineIds, setDeletedLineIds] = useState([]);
  const [shops, setShops] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const newLineKeyRef = useRef(0);

  // View/edit + dirty tracking
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [receivingOpen, setReceivingOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [paymentPromptOpen, setPaymentPromptOpen] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [draftChecked, setDraftChecked] = useState(false);
  // Generic confirm dialog: { title, message, primary: {label, onClick, danger}, secondary?: {label, onClick} } | null
  const [confirm, setConfirm] = useState(null);

  const draftKey = id ? `purchase-order-draft:${id}` : null;

  // Block navigation when dirty
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    document.title = 'Purchase Order';
  }, []);

  // Warn on tab close / refresh when dirty (this dialog can't be customized — browser native)
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchPO = async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const [poRes, linesRes, shopsRes, jobsRes, receiptsRes] = await Promise.all([
        supabase
          .from('purchase_orders')
          .select(`
            *,
            suppliers(id, name, vendor_code, contact_name, contact_email, contact_phone, address, city, state, zip, payment_terms, default_ship_via),
            shops(id, name, ship_to_name, ship_to_address, ship_to_city, ship_to_state, ship_to_zip, ship_to_contact_name, ship_to_contact_phone, ship_to_contact_email)
          `)
          .eq('id', id)
          .single(),
        supabase
          .from('purchase_order_lines')
          .select('*')
          .eq('purchase_order_id', id)
          .order('line_number'),
        supabase.from('shops').select('id, name, ship_to_name, ship_to_address, ship_to_city, ship_to_state, ship_to_zip, ship_to_contact_name, ship_to_contact_phone, ship_to_contact_email').order('name'),
        supabase
          .from('jobs')
          .select('id, po_number, po_line_number, part_number, description, status')
          .neq('status', 'closed')
          .order('po_number', { ascending: false })
          .limit(500),
        supabase
          .from('purchase_order_receipts')
          .select('id, po_line_id, lot_number, received_quantity, received_date, packing_slip_ref, notes, created_at')
          .eq('purchase_order_id', id)
          .order('received_date', { ascending: false })
          .order('created_at', { ascending: false }),
      ]);

      if (poRes.error) throw poRes.error;
      if (linesRes.error) throw linesRes.error;
      if (shopsRes.error) throw shopsRes.error;
      if (jobsRes.error) throw jobsRes.error;
      if (receiptsRes.error) throw receiptsRes.error;

      setPo(poRes.data);
      setSupplier(poRes.data.suppliers || null);
      setLines((linesRes.data || []).map((l) => ({
        ...l,
        _key: `db-${l.id}`,
        quantity: l.quantity ?? '',
        unit_price: l.unit_price ?? '',
        line_number: l.line_number ?? '',
        need_by: l.need_by ?? '',
      })));
      setShops(shopsRes.data || []);
      setJobs(jobsRes.data || []);
      setReceipts(receiptsRes.data || []);
      setDeletedLineIds([]);
      setIsDirty(false);
      // Drafts open in edit mode; sent/received/cancelled open in view mode
      setIsEditMode(poRes.data.status === 'draft');
    } catch (err) {
      console.error('Failed to load PO:', err);
      toast.error('Failed to load PO.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const refreshPO = () => fetchPO(true);

  useEffect(() => {
    if (user) fetchPO();
    // Depend on user?.id (stable) — `user` object reference changes on Supabase
    // auth token refresh, which would otherwise wipe in-progress edits on tab focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  // Restore unsaved local draft (if any) after initial fetch
  useEffect(() => {
    if (!po || draftChecked || !draftKey) return;
    const draft = readLocalDraft(draftKey);
    if (draft?.data?.po && draft.data.po.id === id) {
      setPo(draft.data.po);
      setLines(draft.data.lines || []);
      setDeletedLineIds(draft.data.deletedLineIds || []);
      setIsDirty(true);
      setIsEditMode(true);
      setRestoredFromDraft(true);
    }
    setDraftChecked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po?.id, draftKey, draftChecked]);

  // Auto-save edits to localStorage
  const draftSnapshot = useMemo(
    () => ({ po, lines, deletedLineIds }),
    [po, lines, deletedLineIds]
  );
  useLocalDraft({ key: draftKey, isDirty, snapshot: draftSnapshot });

  const dismissDraft = async () => {
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    await fetchPO();
  };

  const totals = useMemo(() => {
    const subtotal = lines.reduce(
      (sum, l) => sum + Number(l.quantity || 0) * Number(l.unit_price || 0),
      0
    );
    return { subtotal, total: subtotal };
  }, [lines]);

  const shopShipTo = useMemo(() => {
    if (po?.shop_id == null || po?.shop_id === '') return null;
    // shop_id is integer in DB but may be a string when set via the <select> dropdown
    const sh = shops.find((s) => String(s.id) === String(po.shop_id));
    return sh || null;
  }, [shops, po?.shop_id]);

  // ---------- mutation helpers (mark dirty automatically) ----------
  const setPoField = (field) => (e) => {
    const val = e?.target?.value ?? e;
    setPo((p) => ({ ...p, [field]: val }));
    setIsDirty(true);
  };

  const handleVendorChange = (supplierId, vendorRow) => {
    setPo((p) => ({ ...p, supplier_id: supplierId }));
    setSupplier(vendorRow);
    setIsDirty(true);
  };

  const addLine = () => {
    const nextLineNum = (lines.reduce((max, l) => Math.max(max, parseInt(l.line_number) || 0), 0)) + 1;
    newLineKeyRef.current += 1;
    setLines((ls) => [
      ...ls,
      { ...blankLine(), _key: `new-${newLineKeyRef.current}`, line_number: String(nextLineNum), uom: 'ea' },
    ]);
    setIsDirty(true);
  };

  const updateLine = (idx, patch) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setIsDirty(true);
  };

  const removeLine = (idx) => {
    setLines((ls) => {
      const line = ls[idx];
      if (line?.id) {
        setDeletedLineIds((ids) => [...ids, line.id]);
      }
      return ls.filter((_, i) => i !== idx);
    });
    setIsDirty(true);
  };

  // ---------- persistence ----------
  const buildLinePayload = (l) => ({
    purchase_order_id: id,
    line_number: parseInt(l.line_number) || 0,
    description: l.description?.trim() || null,
    part_number: l.part_number?.trim() || null,
    quantity: Number(l.quantity || 0),
    uom: l.uom || 'ea',
    unit_price: Number(l.unit_price || 0),
    extended_price: Number(l.quantity || 0) * Number(l.unit_price || 0),
    need_by: l.need_by || null,
    intended_job_id: l.intended_job_id || null,
    notes: l.notes?.trim() || null,
  });

  const saveCore = async () => {
    if (!po) return false;
    if (!po.po_number?.trim()) {
      toast.error('PO number is required.');
      return false;
    }

    // Guardrail: if user is trying to save with status=issued, validate
    if (po.status === 'issued') {
      const err = validateForSend();
      if (err) {
        toast.error(err);
        return false;
      }
    }

    const headerUpdate = {
      po_number: po.po_number.trim(),
      supplier_id: po.supplier_id || null,
      shop_id: po.shop_id || null,
      po_date: po.po_date,
      payment_terms: po.payment_terms?.trim() || null,
      ship_via: po.ship_via?.trim() || null,
      notes_to_vendor: po.notes_to_vendor?.trim() || null,
      internal_notes: po.internal_notes?.trim() || null,
      status: po.status || 'draft',
      subtotal: totals.subtotal,
      total: totals.total,
    };
    if (po.status === 'issued' && !po.issued_at) {
      headerUpdate.issued_at = new Date().toISOString();
    }
    if ((po.status === 'received' || po.status === 'cancelled') && !po.closed_at) {
      headerUpdate.closed_at = new Date().toISOString();
    }

    // Payment fields
    headerUpdate.payment_status = po.payment_status || 'unpaid';
    headerUpdate.payment_reference = po.payment_reference?.trim() || null;
    if (po.payment_status === 'paid' && !po.paid_at) {
      headerUpdate.paid_at = new Date().toISOString();
    }
    if (po.payment_status === 'unpaid' && po.paid_at) {
      headerUpdate.paid_at = null;
    }

    const { error: poErr } = await supabase
      .from('purchase_orders')
      .update(headerUpdate)
      .eq('id', id);
    if (poErr) throw poErr;

    if (deletedLineIds.length > 0) {
      const { error: delErr } = await supabase
        .from('purchase_order_lines')
        .delete()
        .in('id', deletedLineIds);
      if (delErr) throw delErr;
    }

    const existing = lines.filter((l) => l.id);
    for (const l of existing) {
      const { error: upErr } = await supabase
        .from('purchase_order_lines')
        .update(buildLinePayload(l))
        .eq('id', l.id);
      if (upErr) throw upErr;
    }

    const newOnes = lines.filter((l) => !l.id);
    if (newOnes.length > 0) {
      const { error: insErr } = await supabase
        .from('purchase_order_lines')
        .insert(newOnes.map(buildLinePayload));
      if (insErr) throw insErr;
    }

    return true;
  };

  const handleSave = async (exitOnSuccess = true) => {
    if (saving) return false;
    setSaving(true);
    try {
      const ok = await saveCore();
      if (!ok) return false;
      toast.success('PO saved.');
      setIsDirty(false);
      clearLocalDraft(draftKey);
      setRestoredFromDraft(false);
      await refreshPO();
      if (exitOnSuccess) setIsEditMode(false);
      return true;
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.message || 'Failed to save PO.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const { error } = await supabase.from('purchase_orders').delete().eq('id', id);
      if (error) throw error;
      clearLocalDraft(draftKey);
      setDeleteOpen(false);
      setIsDirty(false);
      toast.success('PO deleted.');
      navigate('/purchase-orders');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.message || 'Failed to delete PO.');
    }
  };

  const doGeneratePDF = async () => {
    try {
      await generatePOPDF({ po, lines, supplier, shop: shopShipTo });
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate PDF.');
    }
  };

  const handleDownloadPDF = () => doGeneratePDF();

  const buildLabelFromReceipt = (r) => {
    const line = lines.find((l) => l.id === r.po_line_id);
    return {
      lot_number: r.lot_number,
      description: line?.description,
      quantity: Number(r.received_quantity),
      uom: line?.uom,
      vendor_name: supplier?.name,
      po_number: po?.po_number,
      received_date: r.received_date,
    };
  };

  const handlePrintAllLabels = () => {
    printLotLabels(receipts.map(buildLabelFromReceipt));
  };

  const handlePrintOneLabel = (receipt) => {
    printLotLabels([buildLabelFromReceipt(receipt)]);
  };

  // ---------- quick inline status/payment updates (no edit-mode required) ----------
  const validateForSend = () => {
    if (!po?.supplier_id) return 'Pick a vendor before sending.';
    if (!lines || lines.length === 0) return 'Add at least one line before sending.';
    if (lines.every((l) => Number(l.quantity || 0) <= 0)) return 'At least one line must have a quantity.';
    return null;
  };

  const persistStatusChange = async (newStatus) => {
    try {
      const update = { status: newStatus };
      if (newStatus === 'issued' && !po.issued_at) update.issued_at = new Date().toISOString();
      if ((newStatus === 'received' || newStatus === 'cancelled') && !po.closed_at) {
        update.closed_at = new Date().toISOString();
      }
      // Reverting clears progression timestamps so they're accurate the next time you progress
      if (isStatusRevert(po.status, newStatus)) {
        if ((STATUS_ORDER[newStatus] ?? 0) < STATUS_ORDER.issued && po.issued_at) update.issued_at = null;
        if ((STATUS_ORDER[newStatus] ?? 0) < STATUS_ORDER.received && po.closed_at) update.closed_at = null;
      }
      const { error } = await supabase.from('purchase_orders').update(update).eq('id', id);
      if (error) throw error;
      toast.success('Status updated.');
      await refreshPO();
    } catch (err) {
      console.error('Status update failed:', err);
      toast.error(err.message || 'Failed to update status.');
    }
  };

  const handleQuickStatusChange = async (newStatus) => {
    if (!po || newStatus === po.status) return;

    // Guardrail: don't allow Send without vendor + lines
    if (newStatus === 'issued') {
      const err = validateForSend();
      if (err) {
        toast.error(err);
        return;
      }
    }

    // Picking Received with outstanding material → just open the receive flow.
    // Status will auto-flip to Received via the DB trigger once all lines are fully received.
    if (newStatus === 'received') {
      const hasOutstanding = lines.some(
        (l) => Number(l.quantity || 0) > Number(l.received_quantity || 0)
      );
      if (hasOutstanding) {
        setReceivingOpen(true);
        return;
      }
    }

    // Warn if cancelling a PO that has receipts
    if (newStatus === 'cancelled' && receipts.length > 0) {
      setConfirm({
        title: 'Cancel PO with receipts?',
        message: `This PO has ${receipts.length} receipt${receipts.length === 1 ? '' : 's'} on record. The material is in inventory; cancelling won't reverse those receipts. Continue?`,
        primary: { label: 'Cancel PO', danger: true, onClick: () => persistStatusChange(newStatus) },
      });
      return;
    }

    // Warn on reverting backward
    if (isStatusRevert(po.status, newStatus)) {
      setConfirm({
        title: 'Revert status?',
        message: `Move from ${STATUS_LABEL[po.status]} back to ${STATUS_LABEL[newStatus]}? Progression timestamps will be cleared.`,
        primary: { label: 'Revert', danger: true, onClick: () => persistStatusChange(newStatus) },
      });
      return;
    }

    await persistStatusChange(newStatus);
  };

  const persistPaymentChange = async (newPayment, reference) => {
    try {
      const update = { payment_status: newPayment };
      if (newPayment === 'paid') {
        if (!po.paid_at) update.paid_at = new Date().toISOString();
        if (reference !== undefined) update.payment_reference = reference;
      }
      if (newPayment === 'unpaid' && po.paid_at) update.paid_at = null;
      const { error } = await supabase.from('purchase_orders').update(update).eq('id', id);
      if (error) throw error;
      toast.success('Payment updated.');
      await refreshPO();
    } catch (err) {
      console.error('Payment update failed:', err);
      toast.error(err.message || 'Failed to update payment.');
    }
  };

  const handleQuickPaymentChange = async (newPayment) => {
    if (!po || newPayment === (po.payment_status || 'unpaid')) return;
    if (newPayment === 'paid') {
      setPaymentPromptOpen(true);
      return;
    }
    await persistPaymentChange(newPayment);
  };

  const confirmPaymentPaid = async (reference) => {
    await persistPaymentChange('paid', reference);
    setPaymentPromptOpen(false);
  };

  // ---------- edit-mode toggles ----------
  const enterEditMode = () => setIsEditMode(true);

  const handleCancelEdit = () => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      setIsEditMode(false);
    }
  };

  const confirmDiscard = async () => {
    setDiscardConfirmOpen(false);
    setIsDirty(false);
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    await fetchPO();
    setIsEditMode(false);
  };

  // ---------- navigation guard handlers ----------
  const blockerStay = () => blocker.reset?.();
  const blockerDiscard = () => {
    setIsDirty(false);
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    blocker.proceed?.();
  };
  const blockerSaveAndLeave = async () => {
    const ok = await handleSave(false);
    if (ok) blocker.proceed?.();
  };

  if (loading) {
    return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  }
  if (!po) {
    return (
      <div className="p-4">
        <Link to="/purchase-orders" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline">
          <ArrowLeftIcon className="w-4 h-4" /> Back to POs
        </Link>
        <p className="mt-4 text-sm text-slate-500">PO not found.</p>
      </div>
    );
  }

  const readOnly = !isEditMode;

  return (
    <div className="p-4 space-y-4 max-w-6xl">
      <Link to="/purchase-orders" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline">
        <ArrowLeftIcon className="w-4 h-4" /> Back to POs
      </Link>

      {restoredFromDraft && (
        <div className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-between">
          <span>Restored unsaved changes from a previous session.</span>
          <button onClick={dismissDraft} className="text-rose-700 hover:underline ml-3">
            Discard local changes
          </button>
        </div>
      )}

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-semibold text-slate-800">{po.po_number}</h1>
            <PillSelect
              value={po.status || 'draft'}
              onChange={(e) => readOnly ? handleQuickStatusChange(e.target.value) : setPoField('status')(e)}
              options={STATUS_OPTIONS}
              pillClass={STATUS_PILL[po.status] || 'bg-slate-100 text-slate-700 border-slate-300'}
            />
            <PillSelect
              value={po.payment_status || 'unpaid'}
              onChange={(e) => readOnly ? handleQuickPaymentChange(e.target.value) : setPoField('payment_status')(e)}
              options={PAYMENT_OPTIONS}
              pillClass={PAYMENT_PILL[po.payment_status] || PAYMENT_PILL.unpaid}
            />
            {isDirty && (
              <span className="text-xs text-amber-700 italic">· unsaved changes</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {readOnly ? (
            <button
              onClick={enterEditMode}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800"
            >
              <PencilSquareIcon className="w-4 h-4" /> Edit
            </button>
          ) : (
            <>
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          )}
          {readOnly && (po.status === 'issued' || po.status === 'partially_received') && (
            <button
              onClick={() => setReceivingOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-emerald-700 text-white rounded-sm hover:bg-emerald-800"
            >
              <InboxArrowDownIcon className="w-4 h-4" /> Receive
            </button>
          )}
          {readOnly && receipts.length > 0 && (
            <button
              onClick={handlePrintAllLabels}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50"
              title={`Print ${receipts.length} lot label${receipts.length === 1 ? '' : 's'}`}
            >
              <PrinterIcon className="w-4 h-4" /> Labels
            </button>
          )}
          {readOnly && (
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" /> PDF
            </button>
          )}
          {po.status === 'draft' && (
            <button
              onClick={() => setDeleteOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-500 border border-slate-200 rounded-sm hover:bg-rose-50 hover:text-rose-700"
              aria-label="Delete PO"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Header form */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <FormGrid columns={2}>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Vendor</label>
            <VendorPicker
              value={po.supplier_id}
              onChange={handleVendorChange}
              placeholder="Select vendor…"
              isDisabled={readOnly}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Shop</label>
            <select
              value={po.shop_id || ''}
              onChange={setPoField('shop_id')}
              disabled={readOnly}
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-50 disabled:text-slate-500"
            >
              <option value="">— Select shop —</option>
              {shops.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </FormGrid>

        <div className="mt-4">
          <FormGrid columns={2}>
            <Input label="PO Number" value={po.po_number || ''} onChange={setPoField('po_number')} required disabled={readOnly} />
            <Input label="PO Date" type="date" value={po.po_date || ''} onChange={setPoField('po_date')} required disabled={readOnly} />
          </FormGrid>
        </div>

        <div className="mt-4">
          <FormGrid columns={2}>
            <Select
              label={`Payment Terms${supplier?.payment_terms ? ` — vendor default: ${supplier.payment_terms}` : ''}`}
              value={po.payment_terms || ''}
              onChange={setPoField('payment_terms')}
              options={PAYMENT_TERMS_OPTIONS}
              placeholder="— Select —"
              disabled={readOnly}
            />
            <Input
              label="Ship Via"
              value={po.ship_via || ''}
              onChange={setPoField('ship_via')}
              placeholder={supplier?.default_ship_via ? `Vendor default: ${supplier.default_ship_via}` : 'e.g. UPS Ground'}
              disabled={readOnly}
            />
          </FormGrid>
        </div>
      </div>

      {/* Vendor + ship-to preview */}
      <FormGrid columns={2}>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Vendor</h3>
            {supplier ? (
              <div className="text-sm text-slate-700 space-y-0.5">
                <div className="font-medium">{supplier.name}</div>
                {supplier.contact_name && <div>{supplier.contact_name}</div>}
                {supplier.address && <div>{supplier.address}</div>}
                {(supplier.city || supplier.state || supplier.zip) && (
                  <div>{[supplier.city, supplier.state].filter(Boolean).join(', ')} {supplier.zip || ''}</div>
                )}
                {supplier.contact_phone && <div className="text-slate-500">{supplier.contact_phone}</div>}
                {supplier.contact_email && <div className="text-slate-500">{supplier.contact_email}</div>}
              </div>
            ) : <div className="text-sm text-slate-400">No vendor selected.</div>}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Ship To</h3>
            {shopShipTo ? (
              <div className="text-sm text-slate-700 space-y-0.5">
                <div className="font-medium">{shopShipTo.ship_to_name || shopShipTo.name}</div>
                {shopShipTo.ship_to_contact_name && <div>{shopShipTo.ship_to_contact_name}</div>}
                {shopShipTo.ship_to_address && <div>{shopShipTo.ship_to_address}</div>}
                {(shopShipTo.ship_to_city || shopShipTo.ship_to_state || shopShipTo.ship_to_zip) && (
                  <div>
                    {[shopShipTo.ship_to_city, shopShipTo.ship_to_state].filter(Boolean).join(', ')}{' '}
                    {shopShipTo.ship_to_zip || ''}
                  </div>
                )}
                {shopShipTo.ship_to_contact_phone && <div className="text-slate-500">{shopShipTo.ship_to_contact_phone}</div>}
                {shopShipTo.ship_to_contact_email && <div className="text-slate-500">{shopShipTo.ship_to_contact_email}</div>}
              </div>
            ) : <div className="text-sm text-slate-400">No shop selected.</div>}
          </div>
      </FormGrid>

      {/* Lines */}
      <POLineEditor
        lines={lines}
        jobs={jobs}
        disabled={readOnly}
        onChange={updateLine}
        onAdd={addLine}
        onRemove={removeLine}
      />

      {/* Receipts (lots received) */}
      {receipts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Receipts ({receipts.length})
            </h3>
          </div>
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500">Lot #</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-24">Date</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500">Line / Description</th>
                <th className="px-2 py-1.5 text-right text-xs font-medium text-slate-500 w-20">Qty</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500 w-32">Packing Slip</th>
                <th className="px-2 py-1.5 text-left text-xs font-medium text-slate-500">Notes</th>
                <th className="px-2 py-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {receipts.map((r) => {
                const line = lines.find((l) => l.id === r.po_line_id);
                return (
                  <tr
                    key={r.id}
                    onClick={() => setEditingReceipt(r)}
                    className="hover:bg-slate-50 cursor-pointer group"
                    title="Click to edit receipt"
                  >
                    <td className="px-2 py-1.5 text-xs font-mono text-slate-700">{r.lot_number}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-600">
                      {r.received_date ? new Date(r.received_date + 'T00:00:00').toLocaleDateString() : '-'}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-slate-700">
                      <span className="text-slate-500">Line {line?.line_number || '?'}</span>
                      {line?.description ? ` · ${line.description}` : ''}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-right text-slate-800 tabular-nums">
                      {Number(r.received_quantity)} {line?.uom || ''}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-slate-600">{r.packing_slip_ref || '-'}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-500">{r.notes || ''}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePrintOneLabel(r); }}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-slate-600 border border-slate-200 rounded-sm hover:bg-white hover:border-slate-300 mr-1"
                        title="Print lot label"
                      >
                        <PrinterIcon className="w-3 h-3" /> Print
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingReceipt(r); }}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-slate-700 border border-slate-200 rounded-sm hover:bg-white hover:border-slate-300"
                        title="Edit receipt"
                      >
                        <PencilSquareIcon className="w-3 h-3" /> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals + Notes */}
      <FormGrid columns={2}>
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Total</h3>
            <div className="flex justify-between text-lg">
              <span className="font-semibold text-slate-800">PO Total</span>
              <span className="font-semibold text-slate-900 tabular-nums">{fmtMoney(totals.total)}</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Tax &amp; shipping appear on the vendor's invoice, not the PO.
            </p>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <Input
              label="Payment Reference"
              value={po.payment_reference || ''}
              onChange={setPoField('payment_reference')}
              placeholder="Check #, ACH ref, etc."
              disabled={readOnly}
            />
            {po.paid_at && (
              <p className="mt-1 text-xs text-slate-500">
                Paid on {new Date(po.paid_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <Textarea
            label="Notes to Vendor (prints on PDF)"
            value={po.notes_to_vendor || ''}
            onChange={setPoField('notes_to_vendor')}
            rows={3}
            disabled={readOnly}
          />
          <Textarea
            label="Internal Notes (not printed)"
            value={po.internal_notes || ''}
            onChange={setPoField('internal_notes')}
            rows={3}
            disabled={readOnly}
          />
        </div>
      </FormGrid>

      {/* ---------- Modals ---------- */}

      {/* Navigation block: user tried to leave with unsaved changes */}
      <Modal
        isOpen={blocker.state === 'blocked'}
        onClose={blockerStay}
        title="Unsaved changes"
        size="md"
      >
        <p className="text-sm text-slate-700">
          You have unsaved changes on this PO. What would you like to do?
        </p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={blockerStay} disabled={saving}>
            Stay on page
          </ModalButton>
          <ModalButton variant="danger" onClick={blockerDiscard} disabled={saving}>
            Discard
          </ModalButton>
          <ModalButton variant="primary" onClick={blockerSaveAndLeave} loading={saving}>
            Save &amp; leave
          </ModalButton>
        </ModalFooter>
      </Modal>

      {/* Discard confirm: user clicked Cancel-edit with unsaved changes */}
      <Modal
        isOpen={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        title="Discard changes?"
        size="md"
      >
        <p className="text-sm text-slate-700">
          Your unsaved changes will be lost.
        </p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setDiscardConfirmOpen(false)}>
            Keep editing
          </ModalButton>
          <ModalButton variant="danger" onClick={confirmDiscard}>
            Discard
          </ModalButton>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete PO?"
        size="md"
      >
        <p className="text-sm text-slate-700">
          This will permanently delete <span className="font-medium">{po.po_number}</span> and all its line items.
          This cannot be undone.
        </p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setDeleteOpen(false)}>
            Cancel
          </ModalButton>
          <ModalButton variant="danger" onClick={confirmDelete}>
            Delete PO
          </ModalButton>
        </ModalFooter>
      </Modal>

      {/* Receiving modal */}
      <POReceivingModal
        isOpen={receivingOpen}
        onClose={() => setReceivingOpen(false)}
        po={po}
        lines={lines}
        onReceived={refreshPO}
      />

      {/* Receipt edit / delete modal */}
      <POReceiptEditModal
        isOpen={!!editingReceipt}
        onClose={() => setEditingReceipt(null)}
        receipt={editingReceipt}
        line={editingReceipt ? lines.find((l) => l.id === editingReceipt.po_line_id) : null}
        onSaved={refreshPO}
        onDeleted={refreshPO}
      />

      {/* Payment reference prompt (Paid) */}
      <PaymentReferenceModal
        isOpen={paymentPromptOpen}
        onClose={() => setPaymentPromptOpen(false)}
        poNumber={po.po_number}
        defaultReference={po.payment_reference}
        onConfirm={confirmPaymentPaid}
      />

      {/* Generic confirm dialog */}
      <Modal
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.title || ''}
        size="md"
      >
        <p className="text-sm text-slate-700">{confirm?.message}</p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setConfirm(null)}>
            Cancel
          </ModalButton>
          {confirm?.secondary && (
            <ModalButton
              variant="secondary"
              onClick={() => { confirm.secondary.onClick(); setConfirm(null); }}
            >
              {confirm.secondary.label}
            </ModalButton>
          )}
          {confirm?.primary && (
            <ModalButton
              variant={confirm.primary.danger ? 'danger' : 'primary'}
              onClick={() => { confirm.primary.onClick(); setConfirm(null); }}
            >
              {confirm.primary.label}
            </ModalButton>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}
